<template>
  <div class="home text-left mb-4">
    <!-- Floating UI toggle button (dismissable) -->
    <transition name="fade">
      <div v-if="showFloatingToggle" class="floating-toggle">
        <b-button
          variant="dark"
          size="sm"
          class="floating-toggle-btn shadow"
          @click="toggleUI"
        >
          <b-icon :icon="uiPref === 'classic' ? 'grid-3x3-gap' : 'list-ul'" class="mr-1"></b-icon>
          {{ uiPref === 'classic' ? 'Try Modern View' : 'Classic View' }}
        </b-button>
        <button class="floating-toggle-dismiss" @click="dismissFloatingToggle" title="Dismiss">
          <b-icon icon="x"></b-icon>
        </button>
      </div>
    </transition>

    <!-- An active emergency comes first, in either view (REQ-88) -->
    <emergency-banner :signed_in="signed_in" />

    <home-classic
      v-if="uiPref === 'classic'"
      v-bind="homeProps"
      @nav-to-signin="navToSignIn"
    />
    <home-modern
      v-else
      v-bind="homeProps"
      @nav-to-signin="navToSignIn"
    />
  </div>
</template>

<script>
import HomeClassic from '@/components/HomeClassic.vue'
import HomeModern from '@/components/HomeModern.vue'
import EmergencyBanner from '@/components/EmergencyBanner.vue'
import { currentReportYear, reportYearStart } from '@/reportYear'

const MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.']

// '2026-12-31' -> 'Dec. 31, 2026'
function shortDate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return `${MONTHS[m - 1]} ${d}, ${y}`
}

function isoDate(dt) {
  const pad = n => String(n).padStart(2, '0')
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
}

// A report year's items are due December 31 of the year before it (reportYear.js).
function dueNote(reportYear) {
  const deadline = reportYear - 1 + '-12-31'
  return new Date() > new Date(deadline + 'T23:59:59') ? 'Past due' : 'Due by ' + shortDate(deadline)
}

export default {
  name: "Home",
  components: {
    HomeClassic,
    HomeModern,
    EmergencyBanner
  },
  data() {
    return {
      show_signin_alert: false,
      show_auth_error: false,
      local_auth_error_message: null,
      floatingToggleDismissed: false,
      message_count: 0,
      sigdef_status: {
        available: false,
        unresolved_count: 0,
        pending_response_count: 0,
        needs_attention: false,
      },
      lsli_notice_due: false,
      lsli_notice_year: null,
      boardMembersCorrections: false,
      // RTCR site plan (Randall, 2026-10-07): MSDH asked for corrections, or
      // the 2026 plan is still owed (not submitted, or rejected); nothing
      // while a plan is in review
      rtcrCorrections: false,
      rtcrPlanDue: '',
      // REQ-156: the RTCR button's due note stays until the plan is Accepted,
      // so it also shows while a plan is in review (the panel above does not)
      rtcrButtonDue: '',
      // REQ-35: the system has an active 4-log plant, so it files a monthly residual log.
      fourlogCovered: false,
      // VOC monitoring-waiver eligibility indicator (durable; independent of the
      // temporary Dec-2025 waiver banner block below).
      vocEligible: false,
      vocWaiverSubmitted: false,
      // TEMPORARY: Waiver banner data - can be removed after Dec 31, 2025
      socEligible: false,
      socWaiverSubmitted: false,
      asbestosEligible: false,
      asbestosWaiverSubmitted: false,
      iocEligible: false,
      iocWaiverSubmitted: false,
      // REQ-156 (Karen Walters, 2026-10-20): a small "Due by Dec. 31, 2026" on
      // the Public Water System Report and Board Members buttons while this
      // report year's item is still owed; '' when it isn't. See dueNote().
      annualReportDue: '',
      boardMembersDue: '',
    }
  },
  mounted() {
    //this.getSession();
  },
  watch: {
    // The store can learn the system after Home is created (a reload restores it
    // from IndexedDB), so the 4-log card re-checks when either changes.
    pws_id() { if (this.signed_in) this.getFourlogStatus() },
    signed_in(v) { if (v) this.getFourlogStatus() }
  },
  created() {
    // Load floating toggle dismissed state
    this.floatingToggleDismissed = localStorage.getItem('pws_portal_ui_toggle_dismissed') === 'true'

    console.log('Home: signed_in: ' + this.signed_in)
    console.log('Home: show_alert: ' + this.$store.state.signed_in_alert_shown)
    if (!this.$store.state.signed_in_alert_shown && this.signed_in) {
      this.$store.commit("set_signed_in_alert_shown", true)
      this.show_signin_alert = true
    }
    // Show auth error message if present
    console.log('Home: auth_error_message:', this.auth_error_message)
    if (this.auth_error_message) {
      console.log('Home: showing auth error alert')
      this.local_auth_error_message = this.auth_error_message
      this.show_auth_error = true
      // Clear the error message from store after copying it locally
      this.$store.commit('set_auth_error_message', null)
    }
    if (this.signed_in) {
      this.getMessageCount()
      this.getSigDefStatus()
      this.getLsliNoticeStatus()
      this.getBoardMemberReview()
      this.getRtcrStatus()
      this.getFourlogStatus()
      this.getVocWaiverStatus()
      this.getWaiverStatus() // TEMPORARY: can be removed after Dec 31, 2025
      this.getAnnualReportStatus()
    }
  },
  computed: {
    pws_id() {
      return this.$store.state.pws_id
    },
    role() {
      return this.$store.state.role
    },
    signed_in() {
      return this.$store.state.signed_in
    },
    store_counter() {
      return this.$store.state.counter
    },
    auth_error_message() {
      return this.$store.state.auth_error_message
    },
    uiPref() {
      return this.$store.state.ui_preference
    },
    // VOC monitoring-waiver: eligible and not yet submitted -> surface a durable
    // "needs attention" indicator (modeled on Sig Defs, not the temporary banner).
    vocWaiverActionNeeded() {
      return this.signed_in && this.vocEligible && !this.vocWaiverSubmitted
    },
    // TEMPORARY: These computed properties can be removed after Dec 31, 2025
    waiverDeadlineToday() {
      if (new Date() > new Date('2025-12-31T23:59:59')) return false
      if (!this.signed_in) return false
      const hasPendingSoc = this.socEligible && !this.socWaiverSubmitted
      const hasPendingAsbestos = this.asbestosEligible && !this.asbestosWaiverSubmitted
      const hasPendingIoc = this.iocEligible && !this.iocWaiverSubmitted
      return hasPendingSoc || hasPendingAsbestos || hasPendingIoc
    },
    showFloatingToggle() {
      // Only show in classic view, and only if not dismissed
      return this.uiPref === 'classic' && !this.floatingToggleDismissed
    },
    // Props to pass to child components
    homeProps() {
      return {
        signed_in: this.signed_in,
        pws_id: this.pws_id,
        role: this.role,
        message_count: this.message_count,
        sigdef_status: this.sigdef_status,
        lsli_notice_due: this.lsli_notice_due,
        lsli_notice_year: this.lsli_notice_year,
        vocWaiverActionNeeded: this.vocWaiverActionNeeded,
        waiverDeadlineToday: this.waiverDeadlineToday,
        annualReportDue: this.annualReportDue,
        boardMembersDue: this.boardMembersDue,
        boardMembersCorrections: this.boardMembersCorrections,
        rtcrCorrections: this.rtcrCorrections,
        rtcrPlanDue: this.rtcrPlanDue,
        rtcrButtonDue: this.rtcrButtonDue,
        fourlogCovered: this.fourlogCovered,
        showSigninAlert: this.show_signin_alert,
        showAuthError: this.show_auth_error,
        authErrorMessage: this.local_auth_error_message
      }
    }
  },
  methods: {
    toggleUI() {
      const newPref = this.uiPref === 'classic' ? 'modern' : 'classic'
      this.$store.commit('set_ui_preference', newPref)
    },
    dismissFloatingToggle() {
      this.floatingToggleDismissed = true
      localStorage.setItem('pws_portal_ui_toggle_dismissed', 'true')
    },
    getMessageCount() {
      const path = process.env.VUE_APP_API_URI + "/user-message-count"
      const params = {
        message_status: '',
      }
      this.axios
        .get(path, params)
        .then((res) => {
          console.log(res)
          if (res.data) {
            this.message_count = res.data.message_count
          }
        })
    },
    getSigDefStatus() {
      if (!this.pws_id) return

      const path = process.env.VUE_APP_API_URI + "/sigdefs/status"
      this.axios
        .get(path, { params: { pws_id: this.pws_id } })
        .then((res) => {
          if (res.data) {
            this.sigdef_status = res.data
          }
        })
        .catch((error) => {
          console.error('Error fetching SigDef status:', error)
          // On error, leave feature unavailable
        })
    },
    // MSDH asked for corrections to the board member list this cycle.
    async getBoardMemberReview() {
      if (!this.pws_id) return
      try {
        const path = process.env.VUE_APP_API_URI + '/board-members/review'
        const res = await this.axios.get(path, { params: { pws_id: this.pws_id } })
        const review = res.data && res.data.review
        this.boardMembersCorrections = !!(review && review.corrections_requested)
        // Owed by a system the law covers until MSDH reviews this report year's list,
        // like the PWS Report (Karen Walters, REQ-156, 2026-10-20). 'R' is reviewed as
        // Not Required.
        const reqRes = await this.axios.get(process.env.VUE_APP_API_URI + '/board-members-required',
          { params: { pws_id: this.pws_id } })
        const year = currentReportYear()
        const done = review && review.reviewed && ['A', 'R'].includes(review.status) &&
          review.submitted && review.submitted >= isoDate(reportYearStart(year))
        this.boardMembersDue = reqRes.data.board_members_required && !done ? dueNote(year) : ''
      } catch (error) {
        console.error('Error fetching board member review:', error)
      }
    },
    async getRtcrStatus() {
      if (!this.pws_id) return
      try {
        const path = process.env.VUE_APP_API_URI + '/rtcr-status'
        const res = await this.axios.get(path, { params: { pws_id: this.pws_id } })
        const data = res.data || {}
        const req = data.requirement || {}
        this.rtcrCorrections = !!data.corrections
        const note = req.past_due ? 'Past due' : 'Due by ' + shortDate(req.deadline)
        const owed = req.required && ['not_submitted', 'rejected'].includes(req.state)
        this.rtcrPlanDue = owed ? note : ''
        this.rtcrButtonDue = req.required && req.state !== 'met' ? note : ''
      } catch (error) {
        console.error('Error fetching RTCR status:', error)
      }
    },
    // REQ-35: offer the 4-log residual log only to systems with a 4-log plant.
    async getFourlogStatus() {
      if (!this.pws_id) return
      try {
        const path = process.env.VUE_APP_API_URI + '/fourlog/plants'
        const res = await this.axios.get(path, { params: { pws_id: this.pws_id } })
        this.fourlogCovered = !!(res.data && res.data.covered)
      } catch (error) {
        this.fourlogCovered = false
      }
    },
    async getLsliNoticeStatus() {
      if (!this.pws_id) return
      try {
        const path = process.env.VUE_APP_API_URI + '/lsli-notice-status'
        const res = await this.axios.get(path, { params: { pws_id: this.pws_id } })
        if (res.data) {
          this.lsli_notice_due = res.data.due
          this.lsli_notice_year = res.data.notice_year
        }
      } catch (error) {
        console.error('Error fetching LSLI notice status:', error)
      }
    },
    // TEMPORARY: This method can be removed after Dec 31, 2025
    // VOC waiver eligibility indicator — durable (no expiration gate).
    getVocWaiverStatus() {
      if (!this.pws_id) return
      Promise.allSettled([
        this.axios.get(process.env.VUE_APP_API_URI + '/voc-waiver-eligibility', {
          params: { pws_id: this.pws_id }
        }),
        this.axios.get(process.env.VUE_APP_API_URI + '/voc-waiver-status', {
          params: { pws_id: this.pws_id }
        })
      ]).then(([eligibility, status]) => {
        if (eligibility.status === 'fulfilled' && eligibility.value.data) {
          this.vocEligible = eligibility.value.data.eligible
        }
        if (status.status === 'fulfilled' && status.value.data) {
          this.vocWaiverSubmitted = status.value.data.status !== 'Not Completed'
        }
      }).catch((error) => {
        console.error('Error fetching VOC waiver status:', error)
      })
    },
    async getWaiverStatus() {
      // Skip API calls after expiration date
      const expirationDate = new Date('2025-12-31T23:59:59')
      if (new Date() > expirationDate) return

      if (!this.pws_id) return

      try {
        // Check eligibility and waiver status for SOC, Asbestos, and IOC in parallel
        const [socEligibility, socStatus, asbestosEligibility, asbestosStatus, iocEligibility, iocStatus] = await Promise.allSettled([
          this.axios.get(process.env.VUE_APP_API_URI + '/soc-waiver-eligibility', {
            params: { pws_id: this.pws_id }
          }),
          this.axios.get(process.env.VUE_APP_API_URI + '/soc-survey-status', {
            params: { pws_id: this.pws_id }
          }),
          this.axios.get(process.env.VUE_APP_API_URI + '/asbestos-waiver-eligibility', {
            params: { pws_id: this.pws_id }
          }),
          this.axios.get(process.env.VUE_APP_API_URI + '/asbestos-waiver-status', {
            params: { pws_id: this.pws_id }
          }),
          this.axios.get(process.env.VUE_APP_API_URI + '/ioc-waiver-eligibility', {
            params: { pws_id: this.pws_id }
          }),
          this.axios.get(process.env.VUE_APP_API_URI + '/monitoring-waiver-status', {
            params: { pws_id: this.pws_id }
          })
        ])

        if (socEligibility.status === 'fulfilled' && socEligibility.value.data) {
          this.socEligible = socEligibility.value.data.eligible
        }
        if (socStatus.status === 'fulfilled' && socStatus.value.data) {
          this.socWaiverSubmitted = socStatus.value.data.status !== 'Not Completed'
        }
        if (asbestosEligibility.status === 'fulfilled' && asbestosEligibility.value.data) {
          this.asbestosEligible = asbestosEligibility.value.data.eligible
        }
        if (asbestosStatus.status === 'fulfilled' && asbestosStatus.value.data) {
          this.asbestosWaiverSubmitted = asbestosStatus.value.data.status !== 'Not Completed'
        }
        if (iocEligibility.status === 'fulfilled' && iocEligibility.value.data) {
          this.iocEligible = iocEligibility.value.data.eligible
        }
        if (iocStatus.status === 'fulfilled' && iocStatus.value.data) {
          this.iocWaiverSubmitted = iocStatus.value.data.status !== 'Not Completed'
        }
      } catch (error) {
        console.error('Error fetching waiver status:', error)
      }
    },
    // Owed until MSDH has reviewed this report year's PWS Report (Karen, REQ-156):
    // a report sent but not yet reviewed, or sent back, still shows the note.
    async getAnnualReportStatus() {
      if (!this.pws_id) return
      try {
        const path = process.env.VUE_APP_API_URI + '/annual-report-pws-list'
        const response = await this.axios.get(path, { params: { pws_id: this.pws_id } })
        const year = currentReportYear()
        const reviewed = response.data.some(ar => ar.report_year === year && ar.status === 'R')
        this.annualReportDue = reviewed ? '' : dueNote(year)
      } catch (error) {
        console.error('Error fetching annual report status:', error)
      }
    },
    getSession() {
      console.log("checking session")
      const path = process.env.VUE_APP_API_URI + "/getsession"
      this.axios
        .get(path, { withCredentials: true })
        .then((res) => {
          console.log(res)
          if (res.data.pws_id) {
            //this.pws_user_id =ores.data.pws_id;
            this.$store.commit("set_pws_id", res.data.pws_id)
            this.$store.commit("set_role", res.data.role)
            this.$store.commit("set_signed_in", true)
            //this.$router.push({ name: 'Home'})
          } else {
            console.log("not logged in")
          }
        })
        .catch((error) => {
          console.error(error)
        })
    },
    navToSignIn() {
      this.$router.push({ path: '/signin' })
    }
  }
}
</script>
<style scoped>
/* Floating toggle button */
.floating-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 4px;
}

.floating-toggle-btn {
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.85rem;
}

.floating-toggle-dismiss {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease;
}

.floating-toggle-dismiss:hover {
  background: rgba(0, 0, 0, 0.7);
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
